import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/api";
import publicAxiosInstance from "../services/publicApi";
import { API_ENDPOINTS } from "../constants/actionTypes";

// Thunk for fetching ALL companies
export const fetchAllCompanies = createAsyncThunk(
  "company/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.COMPANY.SELECT_ALL,
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ Companies API Error:",
        error.response?.status,
        error.response?.data,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch companies",
      );
    }
  },
);

// Thunk for fetching Company Dropdown (Public - No Auth)
export const fetchCompanyDropdownPublic = createAsyncThunk(
  "company/fetchDropdownPublic",
  async (_, { rejectWithValue }) => {
    try {
      const response = await publicAxiosInstance.get(
        API_ENDPOINTS.COMPANY.DROPDOWN,
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ Public Company Dropdown API Error:",
        error.response?.status || "No Response",
        error.response?.data || error.message,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch company dropdown",
      );
    }
  },
);

// Thunk for fetching company by ID
export const fetchCompanyById = createAsyncThunk(
  "company/fetchById",
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.COMPANY.SELECT, {
        companyId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Company not found",
      );
    }
  },
);

// Thunk for adding a new company
export const addCompany = createAsyncThunk(
  "company/add",
  async (companyData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.COMPANY.INSERT,
        companyData,
      );
      dispatch(fetchAllCompanies());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add company",
      );
    }
  },
);

// Thunk for updating an existing company
export const updateCompany = createAsyncThunk(
  "company/update",
  async (companyData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.COMPANY.UPDATE,
        companyData,
      );
      dispatch(fetchAllCompanies()); // Refresh list after update
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update company",
      );
    }
  },
);

// Thunk for deleting a company
export const deleteCompany = createAsyncThunk(
  "company/delete",
  async (companyId, { rejectWithValue, dispatch }) => {
    try {
      await axiosInstance.delete(
        `${API_ENDPOINTS.COMPANY.DELETE}?id=${companyId}`,
      );
      dispatch(fetchAllCompanies());
      return companyId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete company",
      );
    }
  },
);

const companySlice = createSlice({
  name: "company",
  initialState: {
    companiesList: [], // Flat array
    companyDropdown: [], // Dropdown array
    pagination: null,
    selectedCompany: null,
    isLoading: false,
    error: null,
  },
  reducers: {
    clearSelectedCompany: (state) => {
      state.selectedCompany = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Companies - FIXED
      .addCase(fetchAllCompanies.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllCompanies.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.companiesList = apiData?.data || apiData || [];
        state.pagination = apiData;
      })
      .addCase(fetchAllCompanies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch companies";
      })
      // Fetch Company Dropdown Public
      .addCase(fetchCompanyDropdownPublic.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanyDropdownPublic.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.companyDropdown =
          apiData?.data || apiData || action.payload || [];
      })
      .addCase(fetchCompanyDropdownPublic.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch company dropdown";
      })
      // Fetch By ID
      .addCase(fetchCompanyById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanyById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedCompany = action.payload.data;
      })
      .addCase(fetchCompanyById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add Company
      .addCase(addCompany.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addCompany.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Company
      .addCase(updateCompany.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCompany.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete Company
      .addCase(deleteCompany.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCompany.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSelectedCompany } = companySlice.actions;
export default companySlice.reducer;
